import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../../components/Navbar/Navbar';
import Footer from '../../../components/Footer/Footer';
import StickyBottomNav from '../../../components/Navbar/StickyBottomNav';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { FileText, AlertCircle } from 'lucide-react';
import 'react-lazy-load-image-component/src/effects/blur.css';

const NewsletterGallery = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchNewsletters = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/newsletters`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        setNewsletters(data);
      } catch (error) {
        console.error('Error fetching newsletters:', error);
        setError('Failed to load newsletters. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletters();
  }, [apiUrl]);

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">Newsletters</h1>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Stay updated with our latest news, events, and initiatives
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
              <p className="text-textPrimary">Loading newsletters...</p>
            </div>
          ) : error ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">Something went wrong</h3>
              <p className="text-textPrimary mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
            </motion.div>
          ) : newsletters.length === 0 ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-lightTeal flex items-center justify-center mb-4">
                <FileText size={32} className="text-primary" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">No Newsletters Found</h3>
              <p className="text-textPrimary">Newsletters will appear here as they become available.</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {newsletters.map((newsletter, index) => (
                <motion.div
                  key={newsletter.name}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                >
                  <Link
                    to={`/gallery/newsletters/${encodeURIComponent(newsletter.name)}`}
                    state={{ pdfId: newsletter.pdfId }}
                    className="block h-full"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      {newsletter.thumbnailId ? (
                        <LazyLoadImage
                          alt={newsletter.name}
                          effect="blur"
                          src={`${apiUrl}/api/image/${newsletter.thumbnailId}`}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          placeholderSrc="/placeholder-image.jpg"
                        />
                      ) : (
                        <div className="w-full h-full bg-lightTeal flex items-center justify-center">
                          <FileText size={48} className="text-primary opacity-50" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-h3 text-secondary mb-2">
                        {newsletter.name}
                      </h3>
                      <p className="text-textPrimary mb-4">Newsletter</p>
                      <div className="w-12 h-1 bg-cta"></div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default NewsletterGallery;